import React from "react";
import { withStyles, withTheme } from "@material-ui/core";
import { compose } from "recompose";
import { withUserProfile } from "../../../../../userProfile/userProfileContext";
import MapView, {
  Marker,
  MapControls,
  MapConstant,
  Direction,
  Popup,
} from "../../../../../lib/Map";
import {
  TerminalMarker,
  NextStopComplianceStopMarker,
  NextStopCompliancePickupMarker,
  NextStopComplianceStopAPTMarker,
  NextStopComplianceStopLGMarker,
  NextStopCompliancePriorityMarker,
  MarkerWithBadge,
} from "../../../../../components/mapComponents/Markers";
import { NextStopComplianceStopPopup } from "../../../../../components/mapComponents/Popups";
import { withAppConfig } from "../../../../../appConfigContext";
import appConstants from "@appConstants";
import { getWayPoints, getLastStop } from "../../../../mapComponents/mapHelper";
import { RouteLegend, MapLegend, SelectRoute } from "./Legend";
import {
  getPlannedStopData,
  getDeviationData,
  getActualStopData,
} from "../NextStopComplianceUtills";
import Tooltip from "../../../../common/StyledToolTip";
import { withSnackbar } from 'notistack';
import GpsPings from "../../../../common/GpsPings";
import { getPickupStopBadgeContent } from "../../RecordsLookup/Utils";

const tooltipStyle = (theme) => ({
  tooltip: {
    padding: 0,
  },
});

const StyledToolTip = withStyles(tooltipStyle)(Tooltip);

const styles = (theme) => ({
  anchorSubModulePart: {
    borderRight: `1px solid ${theme.AppBGColor}`,
    height: "100%",
  },
});
class NextStopComplianceMap extends React.Component {
  constructor(props) {
    super(props);
    this.terminalCode = this.props.userData.userProfile.terminal.code;
    this.routeComplianceDataPlanned = null;
    this.actualRouteData = null;
    this.stopMarkerData = null;
    this.routeData = null;
    this.state = {
      selectedStop: null,
      styleName: null,
      showPlannedRoute: true,
      showActualRoute: true,
      showGPSRoute: false,
      gpsPings: []
    };
  }

  onStopClick = (e) => {
    this.setState({ selectedStop: e });
    this.props.onStopClick(e.data.stopSequence);
  };

  onTerminalClick = () => {
    this.props.onStopClick(0);
  };

  getStopColor = (stop) => {
    const theme = this.props.theme;
    if (this.props.stopDetails.stopId === stop.stopId) {
      return {
        color: theme.selectedStopMarkerColor,
        textColor: stop.isDeviation
          ? theme.deviationStopColor
          : theme.iconColor,
      };
    } else if (stop.isDeviation) {
      return {
        color: theme.deviationStopColor,
        textColor: theme.selectedStopMarkerColor,
      };
    } else {
      return {
        color: theme.iconColor,
        textColor: theme.selectedStopMarkerColor,
      };
    }
  };

  getStopMarker(stop) {
    if (stop.data.stopType === appConstants.STOP_TYPE.Pickup) {
      return NextStopCompliancePickupMarker;
    } else if (stop.data.priority) {
      return NextStopCompliancePriorityMarker;
    } else if (stop.data.isLiftGate) {
      return NextStopComplianceStopLGMarker;
    } else if (stop.data.isApt) {
      return NextStopComplianceStopAPTMarker;
    } else {
      return NextStopComplianceStopMarker;
    }
  }

  renderStopMarker = (stop) => {
    const colors = this.getStopColor(stop);
    const StopMarker = this.getStopMarker(stop);
    const badgeContent = getPickupStopBadgeContent(stop?.data);
    return (
      <StyledToolTip
        title={
          <NextStopComplianceStopPopup
            theme={this.props.theme.themeName}
            mergeIconColor={this.props.theme.headFontColor}
            data={stop}
            isOnHover={true}
          ></NextStopComplianceStopPopup>
        }
      >
        <div>
        <MarkerWithBadge
          MarkerComponent={StopMarker}
          stopSequence={stop.stopSequence}
          textColor={colors.textColor}
          color={colors.color}
          badgeContent={badgeContent}
        />
        </div>
      </StyledToolTip>
    );
  };

  onMapClicked = () => {
    this.setState({
      selectedStop: null,
    });
  };

  getMapStyleName = (styleName) => {
    if (this.state.styleName !== styleName) {
      this.setState({ styleName: styleName });
    }
  };

  onShowRouteChange = (event, type) => {
    this.setState({
      [`show${type}Route`]: event.target.checked
    });
  };

  render() {
    const { showPlannedRoute, showGPSRoute, showActualRoute, selectedStop } = this.state;
    const {
      classes,
      theme,
      configJSON,
      stopDetails,
      value,
      routeComplianceList,
      updateNextStopComplianceSelectedRoute,
      routeComplianceData,
      nextStopComplianceSelectedRoute
    } = this.props;
    const { driverId, dispatchId, driverDispatchTime, routeId, routeStartTime, routeEndTime } = nextStopComplianceSelectedRoute;
    const { location: terminalLocation } = 
    this.props.userData.userProfile.terminal;
    if (this.props.routeComplianceData && stopDetails) {
      this.routeData = routeComplianceData;
      this.routeComplianceDataPlanned = getPlannedStopData(
        routeComplianceData,
        stopDetails.stopSequence,
        theme.palette.primary.main
      );
      this.actualRouteData = getActualStopData(
        routeComplianceData,
        terminalLocation,
        theme.deviationStopColor
      );
      this.stopMarkerData = this.routeData.actualStops.filter(
        (stop) => stop.stopId
      );
    }
    const gpsRouteData = {
      terminal: { terminalCode: this.props.userData.userProfile.terminal.code },
      dispatchDateTime: driverDispatchTime,
      driverId: driverId,
      isLiveDispatch: false,
      dispatchId,
      routeId,
      routeStartTime,
      routeEndTime
    }
    return (
      <div className={classes.anchorSubModulePart}>
        <MapView
          zoom={9}
          initialCenter={terminalLocation}
          center={terminalLocation}
          mapId={appConstants.mapIds.NEXT_STOP_COMPLIANCE_MAP}
          mapTheme={theme.themeName}
          mapConfig={configJSON?.maps}
          onClick={this.onMapClicked}
          getMapStyleName={this.getMapStyleName}
        >
          <MapControls
            controlOptions={{
              zoomControl: {
                position: MapConstant.CONTROL_POSITION.BOTTOM_RIGHT,
              },
              satelliteViewControl: {
                position: MapConstant.CONTROL_POSITION.BOTTOM_RIGHT,
              },
              reCenterMapControl: {
                position: MapConstant.CONTROL_POSITION.BOTTOM_RIGHT,
              },
            }}
          />

          <Marker
            position={terminalLocation}
            animation
            anchorPoint={{ x: 20, y: 20 }}
            zIndex={1}
            forceUpdate={true}
            isActiveShipment={true}
            onClick={this.onTerminalClick}
            >
            <TerminalMarker
              data={this.props.userData.userProfile.terminal}
              color={theme.terminalContentColor}
              fillColor={
                this.routeData &&
                this.routeData.actualStops[0].data?.isTerminal &&
                this.routeData.actualStops[0].isDeviation
                  ? theme.deviationStopColor
                  : null
              }
            />
          </Marker>

          {showPlannedRoute && value && this.routeComplianceDataPlanned.plannedStops?.length > 0 ? (
            <Direction
              refreshOnMapStyleLoaded
              origin={
                !this.routeComplianceDataPlanned.data?.isTerminal
                  ? {
                      lat: this.routeComplianceDataPlanned.data.latitude,
                      lng: this.routeComplianceDataPlanned.data.longitude
                    }
                  : terminalLocation
              }
              destination={getLastStop(this.routeComplianceDataPlanned.plannedStops)}
              waypoints={getWayPoints(this.routeComplianceDataPlanned.plannedStops)}
              strokeColor={this.routeComplianceDataPlanned.color}
              data={this.routeComplianceDataPlanned}
              routeAnimation={false}
              key={this.routeComplianceDataPlanned.routeId}
              zIndex={0}
            />
          ) : null}

          {showActualRoute && this.actualRouteData && this.actualRouteData.stops.length > 0 ? (
            <Direction
              refreshOnMapStyleLoaded
              origin={terminalLocation}
              destination={getLastStop(this.actualRouteData.stops)}
              waypoints={getWayPoints(this.actualRouteData.stops)}
              strokeColor={this.actualRouteData.color}
              routeAnimation={false}
              data={this.actualRouteData}
              key={this.actualRouteData.routeId}
              zIndex={0}
              pathType={appConstants.DOTTED_ROUTE}
            />
          ) : null}

          {configJSON?.enableDriverActualRoute && <GpsPings
            theme={theme}
            styleName={this.state.styleName}
            route={gpsRouteData}
            showActualRoute={showGPSRoute}
          />}

          {this.stopMarkerData?.map((dev) => {
            const stop = getDeviationData(
              routeComplianceData,
              dev.stopSequence,
              this.terminalCode
            );
            return (
              stop && (
                <Marker
                  data={stop}
                  position={{
                    lat: stop.data.latitude,
                    lng: stop.data.longitude,
                  }}
                  key={stop.stopId}
                  zIndex={1}
                  forceUpdate={true}
                  isActiveShipment={false}
                  onClick={this.onStopClick}
                >
                  {this.renderStopMarker(stop)}
                </Marker>
              )
            );
          })}

          {selectedStop &&
            stopDetails &&
            selectedStop.data.stopSequence === stopDetails.stopSequence && (
              <Popup marker={selectedStop.marker}>
                <NextStopComplianceStopPopup
                  theme={theme.themeName}
                  mergeIconColor={theme.headFontColor}
                  data={selectedStop.data}
                  isOnHover={false}
                  ></NextStopComplianceStopPopup>
              </Popup>
            )}

          <MapLegend theme={theme} enableDriverActualRoute={configJSON?.enableDriverActualRoute}/>
          <RouteLegend
            header="Planned stop sequence"
            label="RouteMAX suggested stop sequence"
            type="Planned"
            checked={showPlannedRoute}
            onChange={this.onShowRouteChange}
          />
          <RouteLegend
            header="Actual stop sequence"
            label="Stop sequence driver followed"
            type="Actual"
            checked={showActualRoute}
            onChange={this.onShowRouteChange}
          />
          {configJSON?.enableDriverActualRoute && <RouteLegend
            header="Actual route"
            label="GPS coordinates of the driver"
            type="GPS"
            checked={showGPSRoute}
            onChange={this.onShowRouteChange}
          />}
          <SelectRoute
            selectedRoute={this.props.nextStopComplianceSelectedRoute}
            routeComplianceList={routeComplianceList}
            updateNextStopComplianceSelectedRoute={updateNextStopComplianceSelectedRoute}
          />
        </MapView>
      </div>
    );
  }
}

export default compose(
  withStyles(styles),
  withTheme(),
  withUserProfile,
  withAppConfig,
  withSnackbar
)(NextStopComplianceMap);
